import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../css/oauth.css';

const OAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const code = searchParams.get('code');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');
  const state = searchParams.get('state');
  const scope = searchParams.get('scope');

  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [showCode, setShowCode] = useState(true);

  useEffect(() => {
    document.title = "OAuth2 Authorization Callback | Nicole Pua Property Agent";
  }, []);

  const handleCopyCode = async () => {
    if (!code) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleCopyUrl = async () => {
    try {
      const fullUrl = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = fullUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="oauth-container">
      <div className="oauth-card">
        {/* Header Icon & Title */}
        <div className="oauth-header">
          <div className={`oauth-badge-icon ${error ? 'icon-error' : code ? 'icon-success' : 'icon-warning'}`}>
            <i className={`bi ${error ? 'bi-x-circle-fill' : code ? 'bi-shield-check' : 'bi-exclamation-triangle-fill'}`}></i>
          </div>
          <h1 className="oauth-title">
            {error ? 'Authorization Failed' : code ? 'OAuth2 Authorization Code' : 'No Authorization Code'}
          </h1>
          <p className="oauth-subtitle">
            {error 
              ? 'An error occurred during the OAuth2 authentication flow.' 
              : code 
              ? 'Your authorization code was received successfully. Copy the code below to complete your setup.' 
              : 'No code parameter was found in the authorization callback request.'}
          </p>
        </div>

        {/* Code Content & Copy Button */}
        {code && (
          <div className="oauth-body">
            <div className="code-label-row">
              <span className="code-label">
                <i className="bi bi-key-fill text-gold me-2"></i> Authorization Code:
              </span>
              <button 
                type="button"
                className="btn-toggle-mask" 
                onClick={() => setShowCode(!showCode)}
                title={showCode ? "Mask code" : "Show code"}
              >
                <i className={`bi ${showCode ? 'bi-eye-slash' : 'bi-eye'}`}></i> {showCode ? 'Mask' : 'Show'}
              </button>
            </div>

            <div className="code-box">
              <code className="code-text">
                {showCode ? code : '•'.repeat(Math.min(code.length, 36))}
              </code>
            </div>

            <div className="action-buttons">
              <button 
                onClick={handleCopyCode} 
                className={`btn-copy-code ${copied ? 'copied' : ''}`}
                id="copy-oauth-code-btn"
              >
                <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard-check'}`}></i>
                <span>{copied ? 'Copied to Clipboard!' : 'Copy Code to Clipboard'}</span>
              </button>
            </div>

            {/* Optional Metadata */}
            {(state || scope) && (
              <div className="oauth-metadata">
                {state && (
                  <div className="meta-item">
                    <span className="meta-label">State:</span>
                    <span className="meta-value">{state}</span>
                  </div>
                )}
                {scope && (
                  <div className="meta-item">
                    <span className="meta-label">Scope:</span>
                    <span className="meta-value">{scope}</span>
                  </div>
                )}
              </div>
            )}

            <div className="oauth-instructions">
              <i className="bi bi-info-circle-fill text-gold me-2"></i>
              <span>Copy this authorization code and paste it into your application terminal or setting prompt.</span>
            </div>
          </div>
        )}

        {/* Error Details */}
        {error && (
          <div className="oauth-error-box">
            <div className="error-title">
              <i className="bi bi-exclamation-octagon-fill me-2"></i> Error: {error}
            </div>
            {errorDescription && (
              <div className="error-description">{errorDescription}</div>
            )}
          </div>
        )}

        {/* Navigation & Secondary Actions */}
        <div className="oauth-footer-actions">
          <button onClick={handleCopyUrl} className="btn-secondary-action">
            <i className={`bi ${copiedUrl ? 'bi-check2' : 'bi-link-45deg'}`}></i>
            {copiedUrl ? 'URL Copied!' : 'Copy Full URL'}
          </button>
          <button onClick={handleGoHome} className="btn-home-action">
            <i className="bi bi-house-door-fill me-2"></i> Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
